import { ChevronRight, Search, Ticket, UserRoundPlus, Users } from "lucide-react";
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { Avatar } from "../../components/Avatar";
import { EmptyState, PageError, PageLoader } from "../../components/StateViews";
import { usePagedCollection, COLLECTION_PAGE_SIZE } from "../../hooks/usePagedCollection";
import { CollectionPagination } from "../../components/CollectionPagination";
import { apiEnvelope, queryString } from "../../lib/api";
import { formatRelative } from "../../lib/format";
import type { CustomerRecord } from "../../lib/types";

function normalizeCustomers(value: unknown) {
  if (Array.isArray(value)) return value as CustomerRecord[];
  return ((value as { customers?: CustomerRecord[] })?.customers ?? []);
}

export function AgentCustomersPage() {
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState(params.get("query") ?? "");
  const page = Math.max(1, Math.floor(Number(params.get("page")) || 1));
  function changePage(page: number) {
    setParams(current => { const next = new URLSearchParams(current); if (page === 1) next.delete("page"); else next.set("page", String(page)); return next; }, { replace: true });
  }
  const result = usePagedCollection<CustomerRecord>(query, page, changePage, async (page, signal) => {
    const response = await apiEnvelope<unknown>(`/agent/customers${queryString({ query, page, limit: COLLECTION_PAGE_SIZE })}`, { signal });
    const customers = normalizeCustomers(response.data);
    return { items: customers, total: response.meta.total ?? customers.length };
  });

  function submitQuery(value: string) {
    setQuery(value);
    const next = new URLSearchParams(params);
    if (value) next.set("query", value); else next.delete("query");
    next.delete("page");
    setParams(next, { replace: true });
  }

  return (
    <section ref={result.topRef} className="agent-page page-stack">
      <header className="agent-page-header"><div><p className="eyebrow">Customer directory</p><h1>Customers</h1><p>Find requesters and see their support history.</p></div><span className="result-count">{result.total} customers</span></header>
      <div className="toolbar card"><label className="search-field"><Search aria-hidden="true" size={18} /><span className="sr-only">Search customers</span><input value={query} onChange={(event) => submitQuery(event.target.value)} placeholder="Search name, email, or customer ID" /></label></div>
      {result.loading ? <PageLoader label="Loading customers…" /> : result.error ? <PageError message={result.error} onRetry={() => void result.refresh()} /> : result.items.length ? (
        <div className="customer-directory card">
          {result.items.map((customer) => (
            <article key={customer.id}>
              <Avatar name={customer.name} size="medium" />
              <div className="customer-directory__identity"><strong>{customer.name}</strong><span>{customer.email ?? "Email unavailable"}</span></div>
              <div><small>Open tickets</small><strong>{customer.openTickets ?? 0}</strong></div>
              <div><small>Total tickets</small><strong>{customer.totalTickets ?? 0}</strong></div>
              <div><small>Last activity</small><span>{formatRelative(customer.lastSeenAt)}</span></div>
              <Link className="button button--quiet button--small" to={`/agent/customers/${customer.id}`}><Ticket aria-hidden="true" size={15} /> View profile</Link>
              <Link className="customer-directory__mobile-link" to={`/agent/customers/${customer.id}`} aria-label={`View ${customer.name} profile`}><span className="sr-only">View customer profile</span></Link>
              <ChevronRight className="customer-directory__chevron" aria-hidden="true" size={18} />
            </article>
          ))}
        </div>
      ) : <EmptyState title="No customers found" description={query ? "Try another name or email address." : "Customer records appear after their first ticket is created."} icon={query ? <Users aria-hidden="true" size={26} /> : <UserRoundPlus aria-hidden="true" size={26} />} />}
      <CollectionPagination {...result} busy={result.refreshing} count={result.items.length} label="Customer pages" />
    </section>
  );
}
